import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Description, Edit } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import RichTextEditor from "../components/RichTextEditor";
import VariablePicker, { toVariableOptions } from "../components/VariablePicker";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { patientDocumentTypesService, patientDocumentsService, PatientDocumentTypeRow, DocumentTemplateVariable } from "../services/patientDocumentsService";

/**
 * Administrativo → Tipos de documento (RN-537a, item 122): catálogo por entidade dos tipos que a
 * aba Documentos oferece ao emitir, cada um com um TEXTO PADRÃO que preenche o editor. Mesmo
 * esqueleto da página Feriados: filtros reativos, lista com cartões no celular, modal de edição.
 * Tipo já usado não se exclui — inativa (a lista mostra quantos documentos usam cada tipo).
 */

interface FormState {
  id: string | null;
  name: string;
  defaultContent: string;
  order: number;
  active: boolean;
}

const MAX_NAME = 60;
const MAX_CONTENT = 20000;

const DOCUMENT_INTRO =
  "Escreva a variável entre chaves no texto padrão. Quando o documento é emitido pelo cadastro do paciente, o sistema " +
  "troca cada variável pelo dado real e o texto continua editável antes de salvar. Dado que não existe (ou que o perfil " +
  "de quem emite não pode ver) sai como ______, para completar à mão. A data usada é a do campo Data da emissão, que " +
  "pode ser retroativa.";

const DOCUMENT_EXAMPLES = [
  {
    title: "Exemplo — no texto padrão:",
    template: "Declaro que {paciente.nome}, nascido(a) em {paciente.nascimento}, compareceu em {documento.data}, das {atendimento.inicio} às {atendimento.fim}.",
  },
  {
    title: "Assinatura — a impressão não tem linha própria; inclua o bloco no fim do texto:",
    template: "_______________________________\n{emissor.nome}\n{emissor.especialidade} — Registro {emissor.registro}",
    multiline: true,
  },
];

const plainText = (html: string) => html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  color: colors.white,
  "&:hover": { backgroundColor: hover },
});

const PatientDocumentTypes: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState<PatientDocumentTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const [form, setForm] = useState<FormState | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const insertIntoEditor = useRef<((text: string) => void) | null>(null);
  const [variables, setVariables] = useState<DocumentTemplateVariable[]>([]);
  const variableOptions = useMemo(() => toVariableOptions(variables), [variables]);
  const [deleteTarget, setDeleteTarget] = useState<PatientDocumentTypeRow | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await patientDocumentTypesService.getAll());
    } catch {
      showToast("Erro ao carregar os tipos de documento.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Tipos de documento";
    load();
    patientDocumentsService.getVariables().then(setVariables).catch(() => setVariables([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return rows
      .filter((r) => statusFilter === "all" || (statusFilter === "active" ? r.active : !r.active))
      .filter((r) => !term || normalize(`${r.name} ${plainText(r.defaultContent ?? "")}`).includes(term))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  }, [rows, search, statusFilter]);

  const hasFilterChanges = search !== "" || statusFilter !== "all";
  const clearFilters = () => { setSearch(""); setStatusFilter("all"); };

  const openCreate = () => {
    const nextOrder = rows.reduce((max, r) => Math.max(max, r.order), 0) + 1;
    setForm({ id: null, name: "", defaultContent: "", order: nextOrder, active: true });
    setEditorKey((k) => k + 1);
  };

  const openEdit = (row: PatientDocumentTypeRow) => {
    setForm({ id: row.id, name: row.name, defaultContent: row.defaultContent ?? "", order: row.order, active: row.active });
    setEditorKey((k) => k + 1);
  };

  const save = async () => {
    if (!form) return;
    const name = form.name.trim();
    if (!name) { showToast("Informe o nome do tipo.", "error"); return; }
    if (form.defaultContent.length > MAX_CONTENT) { showToast("O texto padrão é longo demais.", "error"); return; }
    setSaving(true);
    try {
      await patientDocumentTypesService.save({
        id: form.id,
        name,
        defaultContent: plainText(form.defaultContent) ? form.defaultContent : null,
        order: form.order,
        active: form.active,
      });
      showToast(form.id ? "Tipo atualizado." : "Tipo incluído.", "success");
      setForm(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar o tipo de documento.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: PatientDocumentTypeRow) => {
    try {
      await patientDocumentTypesService.save({
        id: row.id, name: row.name, defaultContent: row.defaultContent, order: row.order, active: !row.active,
      });
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao alterar o tipo.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await patientDocumentTypesService.remove(deleteTarget.id);
      showToast("Tipo excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao excluir o tipo.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Tipos de documento</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Os tipos oferecidos ao emitir um documento no cadastro do paciente, com o texto padrão que preenche o editor.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Buscar"
                  placeholder="Nome ou texto padrão"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Situação</InputLabel>
                  <Select label="Situação" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} notched displayEmpty>
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="active">Ativos</MenuItem>
                    <MenuItem value="inactive">Inativos</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Novo tipo de documento" />
              </div>
            </div>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: colors.primary }} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
                <Description sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum tipo de documento no filtro</Typography>
                <Typography variant="body2">Ajuste o filtro ou inclua um tipo novo.</Typography>
              </Box>
            ) : (
              <div>
                <ResultCount shown={filtered.length} total={rows.length} />
                <Box className="c4u-table__header">
                  <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 70px" }}>Ordem</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "2 1 200px", minWidth: 0 }}>Tipo</Box>
                  <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "3 1 260px", minWidth: 0 }}>Texto padrão</Box>
                  <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 110px" }}>Documentos</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Situação</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Ações</Box>
                </Box>

                {filtered.map((row) => {
                  const preview = plainText(row.defaultContent ?? "");
                  return (
                    <Box key={row.id} className="c4u-table__row" sx={{ opacity: row.active ? 1 : 0.65 }}>
                      <Typography className="admin-plans-cell c4u-col--lg" data-label="Ordem" sx={{ flex: "0 0 70px", fontSize: "0.85rem", color: colors.textSecondary }}>
                        {row.order}
                      </Typography>
                      <Typography className="admin-plans-cell" data-label="Tipo" sx={{ flex: "2 1 200px", minWidth: 0, fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary }}>
                        {row.name}
                        {row.code ? <Typography component="span" sx={{ fontSize: "0.75rem", color: colors.textSecondary, ml: 1 }}>· Produto</Typography> : null}
                      </Typography>
                      <Typography className="admin-plans-cell c4u-col--md" data-label="Texto padrão" sx={{ flex: "3 1 260px", minWidth: 0, fontSize: "0.85rem", color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {preview ? preview.slice(0, 120) : "—"}
                      </Typography>
                      <Typography className="admin-plans-cell c4u-col--md" data-label="Documentos" sx={{ flex: "0 0 110px", fontSize: "0.85rem", color: colors.textSecondary }}>
                        {row.documentsCount}
                      </Typography>
                      <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 90px" }}>
                        <Chip
                          label={row.active ? "Ativo" : "Inativo"}
                          size="small"
                          onClick={() => toggleActive(row)}
                          title={row.active ? "Clique para inativar (some da emissão; documentos antigos ficam)" : "Clique para ativar"}
                          sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.active ? colors.success : colors.textSecondary }}
                        />
                      </Box>
                      <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 90px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <IconButton className="c4u-row-action" size="small" onClick={() => openEdit(row)} title="Editar" sx={rowActionSx(colors.primary, colors.primaryHover)}>
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        {row.documentsCount === 0 && (
                          <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteTarget(row)} title="Excluir (tipo nunca usado)" sx={rowActionSx("#dc3545", "#c82333")}>
                            <Delete sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={form !== null} onClose={() => setForm(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar tipo de documento" : "Novo tipo de documento"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 120px 160px" }, gap: 2, alignItems: "center" }}>
                <TextField
                  label="Nome do tipo"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: MAX_NAME }}
                  InputLabelProps={{ shrink: true }}
                  placeholder="Ex.: Atestado, Declaração de comparecimento"
                />
                <TextField
                  label="Ordem"
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: Math.max(0, Number(e.target.value) || 0) })}
                  size="small"
                  inputProps={{ min: 0, max: 999 }}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                  control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                  label={form.active ? "Ativo" : "Inativo"}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mb: 0.75 }}>
                  Texto padrão — preenche o editor ao escolher este tipo na emissão. Deixe vazio se o tipo só classifica.
                </Typography>
                <VariablePicker
                  options={variableOptions}
                  onInsert={(text) => insertIntoEditor.current?.(text)}
                  intro={DOCUMENT_INTRO}
                  examples={DOCUMENT_EXAMPLES}
                />
                <RichTextEditor
                  key={`type-${form.id ?? "new"}-${editorKey}`}
                  insertRef={insertIntoEditor}
                  suggestions={variableOptions}
                  value={form.defaultContent}
                  onChange={(html) => setForm((prev) => (prev ? { ...prev, defaultContent: html } : prev))}
                  placeholder="Ex.: Atesto para os devidos fins que o(a) paciente esteve em atendimento nesta clínica..."
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setForm(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Incluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir tipo de documento"
        message={`Excluir "${deleteTarget?.name ?? ""}"? Só é possível porque nenhum documento usa este tipo.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default PatientDocumentTypes;
